import type { InjectionKey } from 'vue';

/**
 * The shape of `GET backoffice/api/{collection}/metrics/{metric}`, mirroring
 * App\Http\Resources\MetricResource.
 */
export type MetricDirection = 'up' | 'down' | 'flat'

export interface MetricDelta {
  label: string
  direction: MetricDirection
}

export interface DistributionRow {
  key: string | null
  label: string
  legend: string | null
  value: number
  formatted: string
  percent: number
  share: number
  delta: MetricDelta | null
}

export interface MetricDrilldown {
  metric: string
  filter: string
  keys: string[]
}

export interface TrendSeries {
  interval: 'minute' | 'half_hour' | 'hour' | 'day' | 'week' | 'month' | 'year'
  period_label: string | null
  points: { date: string, starts_at: string, value: number }[]
}

interface MetricBase {
  title: string
  subtitle: string | null
  note: string | null
}

export interface ValueMetricData extends MetricBase {
  type: 'value'
  value: { value: number | string | null, formatted: string }
  delta: MetricDelta | null
}

export interface PercentageMetricData extends MetricBase {
  type: 'percentage'
  value: { value: number | string, total: number | string | null, percentage: number }
  delta: MetricDelta | null
}

export interface DistributionMetricData extends MetricBase {
  type: 'distribution'
  value: {
    ranked: boolean
    legend: string | null
    rows: DistributionRow[]
    departed: { label: string, share: number }[]
  }
  drilldown: MetricDrilldown | null
}

export interface TrendMetricData extends MetricBase {
  type: 'trend'
  value: TrendSeries
  comparison: TrendSeries | null
}

export type MetricData = ValueMetricData | PercentageMetricData | DistributionMetricData | TrendMetricData

/**
 * How a card draws its reading. A distribution can be drawn either way, so
 * the page picks; every other type has a single drawing.
 */
export type MetricDisplay = 'value' | 'percentage' | 'doughnut' | 'progress-bar' | 'trend'

export type MetricLegendType = 'value' | 'percentage'

/**
 * What a board hands down to its cards: the filters every card is read with.
 */
export interface MetricBoardContext {
  filters: Record<string, string | boolean>
}

export const METRIC_BOARD_KEY: InjectionKey<MetricBoardContext> = Symbol('metricBoard')

/** Taken from App\Support\Metrics\ComparisonColors, so charts and legends agree. */
export const COMPARISON_COLORS = {
  current: 'oklch(51.83% 0.269 263.92)',
  compared: 'oklch(70% 0.03 260)',
}
